/**
 * Service for tarifs (subscription plans) API
 * Handles fetching tarifs by profile type and modules by subscription
 */


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Get tarifs available for a specific profile type
 * @param {number} profileTypeId - Profile type ID
 * @returns {Promise<Array>} List of tarifs with pricing
 */
export const getTarifsByProfileType = async (profileTypeId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tarifs/by-profile-type/${profileTypeId}`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching tarifs by profile type:', error);
        return [];
    }
};

/**
 * Get all tarifs (active only)
 * @returns {Promise<Array>} List of all active tarifs
 */
export const getAllTarifs = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/tarifs`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching all tarifs:', error);
        return [];
    }
};

/**
 * Get modules available for a profile type filtered by subscription type
 * @param {number} profileTypeId - Profile type ID
 * @param {string} subscriptionType - Subscription type (FREE, ARGENT, OR, etc.)
 * @returns {Promise<Array>} List of modules for this subscription level
 */
export const getModulesByProfileTypeAndSubscription = async (profileTypeId, subscriptionType = '') => {
    try {
        let url = `${API_BASE_URL}/profiletype_modules/by-profiletype/${profileTypeId}`;
        if (subscriptionType) {
            url += `?subscriptionType=${encodeURIComponent(subscriptionType)}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        // Filter only active modules
        return success ? data.filter(m => {
            const isActive = m.isActive ?? m.ISACTIVE ?? m.isactive ?? true;
            return isActive === true || isActive === 1;
        }) : [];
    } catch (error) {
        console.error('Error fetching modules by profile type and subscription:', error);
        return [];
    }
};

/**
 * Get user's profile type from localStorage or API
 * @returns {Object|null} Profile type info
 */
export const getUserProfileType = () => {
    const profileTypeId = localStorage.getItem('userProfileTypeId');
    const profileTypeCode = localStorage.getItem('profileTypeCode');

    return {
        id: profileTypeId ? parseInt(profileTypeId, 10) : null,
        code: profileTypeCode || null
    };
};
